/**
 * Admin User View Page
 *
 * Displays detailed information about a single user.
 *
 * @fileoverview Admin user detail page component
 */

import React, { useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  Divider,
  useColorModeValue,
  useToast,
  Skeleton,
  SkeletonText,
  Card,
  CardHeader,
  CardBody,
  Avatar,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useDisclosure,
  Alert,
  AlertIcon,
  Flex,
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdEdit,
  MdPersonOff,
  MdPersonAdd,
  MdDelete,
  MdLockReset,
  MdStar,
  MdChevronRight,
} from 'react-icons/md';
import { useAdminUser } from '../../../hooks/useAdminUsers';
import { useUserProfile } from '../../../hooks/useUserProfile';
import {
  AdminEditUserModal,
  AdminDeactivateUserModal,
  AdminDeleteUserModal,
  AdminManageMembershipsModal,
} from '../../../components/admin';
import type { UpdateAdminUserInput } from '../../../types/admin';

/**
 * Format date for display
 *
 * @param dateString - Date string or null
 * @returns Formatted date string
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Get display name for user
 *
 * @param user - User object
 * @returns Display name string
 */
function getDisplayName(user: { displayName?: string | null; firstName?: string | null; lastName?: string | null; email: string }): string {
  if (user.displayName) return user.displayName;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return user.email.split('@')[0];
}

/**
 * Admin User View Page
 *
 * Displays detailed information about a user including
 * their tenant memberships and allows management actions.
 *
 * @returns React component
 *
 * @example
 * <AdminUserView />
 */
export const AdminUserView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile: userProfile } = useUserProfile();

  const {
    user,
    loading,
    error,
    updateUser,
    deleteUser,
    activateUser,
    deactivateUser,
    sendPasswordReset,
    refetch,
  } = useAdminUser(id);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  // Modal states
  const editModal = useDisclosure();
  const deactivateModal = useDisclosure();
  const deleteModal = useDisclosure();
  const membershipsModal = useDisclosure();

  const isCurrentUser = user?.id === userProfile?.id;

  /**
   * Handle save edit
   */
  const handleSaveEdit = useCallback(async (data: UpdateAdminUserInput): Promise<boolean> => {
    const result = await updateUser(data);
    if (result) {
      toast({
        title: 'User updated',
        description: 'Successfully updated user',
        status: 'success',
        duration: 3000,
      });
      return true;
    }
    return false;
  }, [updateUser, toast]);

  /**
   * Handle activate
   */
  const handleActivate = useCallback(async () => {
    const result = await activateUser();
    if (result) {
      toast({
        title: 'User activated',
        description: 'Successfully activated user',
        status: 'success',
        duration: 3000,
      });
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't activate the user. Please try again.",
        status: 'error',
        duration: 3000,
      });
    }
  }, [activateUser, toast]);

  /**
   * Handle confirm deactivate
   */
  const handleConfirmDeactivate = useCallback(async (): Promise<boolean> => {
    const result = await deactivateUser();
    if (result) {
      toast({
        title: 'User deactivated',
        description: 'Successfully deactivated user',
        status: 'success',
        duration: 3000,
      });
      return true;
    }
    toast({
      title: 'Something went wrong',
      description: "We couldn't deactivate the user. Please try again.",
      status: 'error',
      duration: 3000,
    });
    return false;
  }, [deactivateUser, toast]);

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = useCallback(async (): Promise<boolean> => {
    const result = await deleteUser();
    if (result) {
      toast({
        title: 'User deleted',
        description: 'Successfully deleted user',
        status: 'success',
        duration: 3000,
      });
      navigate('/admin/users');
      return true;
    }
    return false;
  }, [deleteUser, toast, navigate]);

  /**
   * Handle password reset
   */
  const handlePasswordReset = useCallback(async () => {
    const result = await sendPasswordReset();
    if (result) {
      toast({
        title: 'Password reset sent',
        description: result.message,
        status: 'success',
        duration: 3000,
      });
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't send the password reset email. Please try again.",
        status: 'error',
        duration: 3000,
      });
    }
  }, [sendPasswordReset, toast]);

  // Loading state
  if (loading) {
    return (
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <Skeleton h="40px" w="200px" />
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <SkeletonText noOfLines={3} />
                <Skeleton h="100px" />
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error || 'User not found'}
        </Alert>
        <Button mt={4} onClick={() => navigate('/admin/users')}>
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<Icon as={MdChevronRight} color="gray.500" />}
        mb={6}
      >
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/admin/users">
            Users
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>{getDisplayName(user)}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Button
            variant="ghost"
            leftIcon={<Icon as={MdArrowBack} />}
            onClick={() => navigate('/admin/users')}
          >
            Back
          </Button>
          <Heading size="lg">{getDisplayName(user)}</Heading>
          {isCurrentUser && (
            <Badge colorScheme="teal">You</Badge>
          )}
        </HStack>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* User Details Card */}
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">User Details</Heading>
              {!isCurrentUser && (
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    leftIcon={<Icon as={MdEdit} />}
                    onClick={editModal.onOpen}
                  >
                    Edit
                  </Button>
                  {user.isActive ? (
                    <Button
                      size="sm"
                      leftIcon={<Icon as={MdPersonOff} />}
                      colorScheme="orange"
                      variant="outline"
                      onClick={deactivateModal.onOpen}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      leftIcon={<Icon as={MdPersonAdd} />}
                      colorScheme="green"
                      variant="outline"
                      onClick={handleActivate}
                    >
                      Activate
                    </Button>
                  )}
                  <Button
                    size="sm"
                    leftIcon={<Icon as={MdLockReset} />}
                    variant="outline"
                    onClick={handlePasswordReset}
                  >
                    Reset Password
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<Icon as={MdDelete} />}
                    colorScheme="red"
                    variant="outline"
                    onClick={deleteModal.onOpen}
                  >
                    Delete
                  </Button>
                </HStack>
              )}
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <HStack spacing={6} align="flex-start">
              <Avatar size="xl" name={getDisplayName(user)} />
              <VStack align="stretch" spacing={3} flex={1}>
                <HStack>
                  <Text fontWeight="bold" w="120px">Email:</Text>
                  <Text>{user.email}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Display Name:</Text>
                  <Text>{user.displayName || '-'}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">First Name:</Text>
                  <Text>{user.firstName || '-'}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Last Name:</Text>
                  <Text>{user.lastName || '-'}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Global Role:</Text>
                  <HStack>
                    <Badge
                      colorScheme={user.globalRole === 'ADMIN' ? 'purple' : 'gray'}
                    >
                      {user.globalRole}
                    </Badge>
                    {user.globalRole === 'ADMIN' && (
                      <Icon as={MdStar} color="yellow.400" />
                    )}
                  </HStack>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Status:</Text>
                  <Badge colorScheme={user.isActive ? 'green' : 'red'}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Created:</Text>
                  <Text color={mutedColor}>{formatDate(user.createdAt)}</Text>
                </HStack>
                <HStack>
                  <Text fontWeight="bold" w="120px">Last Login:</Text>
                  <Text color={mutedColor}>{formatDate(user.lastLoginAt)}</Text>
                </HStack>
              </VStack>
            </HStack>
          </CardBody>
        </Card>

        {/* Tenant Memberships Card */}
        <Card bg={bgColor} border="1px" borderColor={borderColor}>
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">Tenant Memberships ({user.memberships?.length || 0})</Heading>
              {!isCurrentUser && (
                <Button size="sm" leftIcon={<Icon as={MdEdit} />} onClick={membershipsModal.onOpen}>
                  Manage
                </Button>
              )}
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            {user.memberships?.length === 0 ? (
              <Text color={mutedColor}>No tenant memberships</Text>
            ) : (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Tenant</Th>
                    <Th>Role</Th>
                    <Th>Owner</Th>
                    <Th>Joined</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {user.memberships?.map((membership) => (
                    <Tr key={membership.tenantId}>
                      <Td>
                        <RouterLink to={`/admin/tenants/${membership.tenantId}`}>
                          <Text
                            color="blue.500"
                            _hover={{ textDecoration: 'underline' }}
                          >
                            {membership.tenantName}
                          </Text>
                        </RouterLink>
                      </Td>
                      <Td>
                        <Tag
                          colorScheme={
                            membership.tenantRole === 'TENANT_ADMIN'
                              ? 'blue'
                              : 'gray'
                          }
                        >
                          {membership.tenantRole}
                        </Tag>
                      </Td>
                      <Td>
                        {membership.isOwner ? (
                          <Badge colorScheme="purple">Yes</Badge>
                        ) : (
                          <Text color={mutedColor}>No</Text>
                        )}
                      </Td>
                      <Td>
                        <Text color={mutedColor} fontSize="sm">
                          {formatDate(membership.joinedAt)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Modals */}
      <AdminEditUserModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        user={user}
        onSave={handleSaveEdit}
        loading={loading}
      />

      <AdminDeactivateUserModal
        isOpen={deactivateModal.isOpen}
        onClose={deactivateModal.onClose}
        user={user}
        onConfirm={handleConfirmDeactivate}
        loading={loading}
      />

      <AdminDeleteUserModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        user={user}
        onConfirm={handleConfirmDelete}
        loading={loading}
        error={error}
      />

      <AdminManageMembershipsModal
        isOpen={membershipsModal.isOpen}
        onClose={membershipsModal.onClose}
        user={user}
        onUpdated={(updated) => {
          refetch();
        }}
      />
    </Box>
  );
};

export default AdminUserView;
